import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  KpiCard,
  Modal,
  PageHeader,
  SelectField,
  Spinner,
  TextAreaField,
  TextField,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { Bot, FileText, Globe, HelpCircle, Plus, Trash2, Upload } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { formatRelative } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';

interface Status {
  configured: boolean;
  provider: string | null;
  model: string | null;
  keySource: 'tenant' | 'server' | null;
  dailyTokenLimit: number | null;
  tokensUsedToday: number;
}
interface Agent {
  name: string;
  enabled: boolean;
  mode: 'auto' | 'suggest';
  whatsappEnabled: boolean;
  instagramEnabled: boolean;
  instructions: string;
  handoffKeywords: string[];
}
interface Source {
  id: string;
  kind: 'faq' | 'text' | 'url' | 'pdf';
  title: string;
  status: 'processing' | 'ready' | 'failed';
  error: string | null;
  sourceUrl: string | null;
  chunkCount: number;
  charCount: number;
  createdAt: string;
}
interface Run {
  id: string;
  conversationId: string;
  channel: 'whatsapp' | 'instagram';
  status: 'replied' | 'suggested' | 'escalated' | 'skipped' | 'failed';
  reason: string | null;
  inputTokens: number;
  outputTokens: number;
  sources: string[];
  toolCalls: string[];
  createdAt: string;
}
interface UsageDay {
  day: string;
  inputTokens: number;
  outputTokens: number;
  runs: number;
  replies: number;
  blocked: number;
}
interface Playground {
  reply: string;
  escalate: string | null;
  toolCalls: Array<{ name: string; ok: boolean }>;
  sources: string[];
  inputTokens: number;
  outputTokens: number;
  refused: boolean;
}

const KIND_LABEL: Record<Source['kind'], string> = {
  faq: 'FAQ',
  text: 'Note',
  url: 'Web page',
  pdf: 'PDF',
};
const RUN_LABEL: Record<Run['status'], string> = {
  replied: 'Replied',
  suggested: 'Draft for a person',
  escalated: 'Handed to a person',
  skipped: 'Skipped',
  failed: 'Failed',
};
const TOOL_LABEL: Record<string, string> = {
  search_knowledge: 'looked in your knowledge',
  search_products: 'searched the catalog',
  get_product: 'checked a product',
  get_order_status: 'checked an order',
  escalate_to_human: 'asked for a person',
};

/** The shopping assistant: settings, knowledge, a place to try it, and what it did. */
export function AiAgentsPage() {
  const session = useActiveSession();
  const { can } = useSession();
  const tenantId = session.activeTenant?.id;
  const status = useLoader(() => api.get<Status>('/ai/status'), tenantId);
  const agent = useLoader(() => api.get<{ agent: Agent; exists: boolean }>('/ai/agent'), tenantId);
  const sources = useLoader(
    () => api.get<{ sources: Source[] }>('/ai/knowledge').then((r) => r.sources),
    tenantId,
  );
  const runs = useLoader(
    () => api.get<{ runs: Run[] }>('/ai/runs?limit=20').then((r) => r.runs),
    tenantId,
  );
  const usage = useLoader(
    () => api.get<{ days: UsageDay[] }>('/ai/usage').then((r) => r.days),
    tenantId,
  );
  const manage = can('ai:manage');

  if ((status.loading && !status.data) || (agent.loading && !agent.data)) {
    return <Spinner size="lg" label="Loading" />;
  }
  if (status.error || !status.data || agent.error || !agent.data) {
    return <Alert tone="critical">{status.error ?? agent.error ?? 'Failed to load'}</Alert>;
  }
  const st = status.data;
  const today = usage.data?.[0];
  const used = st.tokensUsedToday;

  return (
    <div className="stack-lg">
      <PageHeader
        title="AI assistant"
        description="A shopping assistant for WhatsApp and Instagram, grounded in your catalog, FAQs and documents. It answers, or drafts replies for your team, and hands over when it should."
      />
      {!st.configured && (
        <Alert tone="warning" title="Add your AI provider">
          The assistant needs an AI provider. Add your own OpenAI or Anthropic API key in{' '}
          <Link to="/settings/ai-provider">Settings → AI provider</Link>. Usage is billed by the
          provider to your account. You can prepare settings and knowledge now; the assistant cannot
          be turned on until a key is added.
        </Alert>
      )}

      <div className="kpi-grid">
        <KpiCard
          label="Assistant"
          value={
            agent.data.agent.enabled
              ? agent.data.agent.mode === 'auto'
                ? 'Answering'
                : 'Drafting'
              : 'Off'
          }
          hint={st.model ? `${st.provider} · ${st.model}` : 'No provider'}
        />
        <KpiCard
          label="Tokens today"
          value={used.toLocaleString()}
          hint={
            st.dailyTokenLimit
              ? `of ${st.dailyTokenLimit.toLocaleString()} daily budget`
              : undefined
          }
        />
        <KpiCard label="Replies today" value={String(today?.replies ?? 0)} />
        <KpiCard label="Questions handled today" value={String(today?.runs ?? 0)} />
      </div>

      <SettingsCard
        key={agent.data.agent.name + String(agent.data.exists)}
        agent={agent.data.agent}
        configured={st.configured}
        canManage={manage}
        onSaved={() => {
          void agent.reload();
          void status.reload();
        }}
      />

      <KnowledgeCard
        sources={sources.data ?? []}
        canManage={manage}
        onChanged={() => void sources.reload()}
      />

      {manage && <Playground disabled={!st.configured} onUsed={() => void status.reload()} />}

      <Card
        title="Recent activity"
        subtitle="The latest 20 times the assistant looked at a message."
        flush
      >
        {(runs.data ?? []).length === 0 ? (
          <EmptyState icon={<Bot size={22} />} title="Nothing yet">
            Once the assistant is on, every message it handles is listed here.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Result</th>
                  <th>Used</th>
                  <th>Tokens</th>
                  <th>When</th>
                  <th>
                    <span className="visually-hidden">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(runs.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Badge
                        tone={
                          r.status === 'replied'
                            ? 'success'
                            : r.status === 'failed'
                              ? 'critical'
                              : r.status === 'escalated'
                                ? 'warning'
                                : 'neutral'
                        }
                      >
                        {RUN_LABEL[r.status]}
                      </Badge>
                      {r.reason && r.reason !== 'running' && (
                        <div className="table__secondary">{r.reason.replace(/_/g, ' ')}</div>
                      )}
                    </td>
                    <td>
                      {r.toolCalls.length === 0 ? (
                        <span className="text-muted">—</span>
                      ) : (
                        [...new Set(r.toolCalls)].map((t) => TOOL_LABEL[t] ?? t).join(', ')
                      )}
                      {r.sources.length > 0 && (
                        <div className="table__secondary">{r.sources.slice(0, 3).join(' · ')}</div>
                      )}
                    </td>
                    <td>{(r.inputTokens + r.outputTokens).toLocaleString()}</td>
                    <td>{formatRelative(r.createdAt)}</td>
                    <td>
                      <Link to={`/inbox?c=${r.conversationId}`}>Open</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function SettingsCard({
  agent,
  configured,
  canManage,
  onSaved,
}: {
  agent: Agent;
  configured: boolean;
  canManage: boolean;
  onSaved: () => void;
}) {
  const toast = useToast();
  const submit = useSubmit();
  const [name, setName] = useState(agent.name);
  const [enabled, setEnabled] = useState(agent.enabled);
  const [mode, setMode] = useState(agent.mode);
  const [wa, setWa] = useState(agent.whatsappEnabled);
  const [ig, setIg] = useState(agent.instagramEnabled);
  const [instructions, setInstructions] = useState(agent.instructions);
  const [keywords, setKeywords] = useState(agent.handoffKeywords.join(', '));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      await api.put('/ai/agent', {
        name,
        enabled,
        mode,
        whatsappEnabled: wa,
        instagramEnabled: ig,
        instructions,
        handoffKeywords: keywords
          .split(/[,\n]/)
          .map((k) => k.trim())
          .filter(Boolean),
      });
      return true;
    });
    if (ok) {
      toast.success('Assistant settings saved');
      onSaved();
    }
  };

  return (
    <Card title="Settings" subtitle="What the assistant does with new customer messages.">
      <form className="stack" onSubmit={(e) => void save(e)}>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <TextField
          label="Assistant name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={submit.fieldErrors['name']}
          disabled={!canManage}
          maxLength={120}
        />
        <Checkbox
          label="Assistant is on"
          description={configured ? undefined : 'add your AI provider key first'}
          checked={enabled}
          disabled={!canManage}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <SelectField
          label="When it is on"
          value={mode}
          disabled={!canManage}
          onChange={(e) => setMode(e.target.value as Agent['mode'])}
          options={[
            { value: 'suggest', label: 'Draft replies for my team to review and send' },
            { value: 'auto', label: 'Answer customers directly' },
          ]}
        />
        <div className="row">
          <Checkbox
            label="WhatsApp"
            checked={wa}
            disabled={!canManage}
            onChange={(e) => setWa(e.target.checked)}
          />
          <Checkbox
            label="Instagram"
            checked={ig}
            disabled={!canManage}
            onChange={(e) => setIg(e.target.checked)}
          />
        </div>
        <TextAreaField
          label="Your instructions"
          rows={5}
          value={instructions}
          disabled={!canManage}
          onChange={(e) => setInstructions(e.target.value)}
          hint="Tone, store policies and anything it must or must not say. It still follows its built-in safety rules."
          maxLength={4000}
        />
        {canManage && instructions.trim() === '' && (
          <div>
            <Button type="button" size="sm" onClick={() => setInstructions(SAMPLE_INSTRUCTIONS)}>
              Fill sample instructions
            </Button>
          </div>
        )}
        <TextField
          label="Hand over immediately when the customer writes"
          value={keywords}
          disabled={!canManage}
          onChange={(e) => setKeywords(e.target.value)}
          hint="Comma separated words or phrases. The assistant stops and a person takes over, without calling the AI."
        />
        <p className="field__hint">
          The assistant only reads: it cannot refund, cancel, change orders or give discounts. It
          shows order details only after the customer is verified, and hands over whenever it is not
          sure.
        </p>
        {canManage && (
          <div className="form-actions">
            <Button type="submit" variant="primary" loading={submit.pending}>
              Save settings
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

type AddKind = 'faq' | 'text' | 'url' | 'pdf';

const SAMPLE_FAQS: { question: string; answer: string }[] = [
  {
    question: 'Do you offer cash on delivery (COD)?',
    answer:
      'Yes, cash on delivery is available on orders up to Rs 5,000. A Rs 40 COD handling fee applies. Prepaid orders by UPI, cards and net banking ship without any extra fee.',
  },
  {
    question: 'How long does delivery take?',
    answer:
      'Metro cities receive orders in 2 to 4 business days and the rest of India in 4 to 7 business days. You get a tracking link on WhatsApp as soon as the order ships.',
  },
  {
    question: 'What is your return and exchange policy?',
    answer:
      'Unused items in original packaging can be returned or exchanged within 7 days of delivery. Refunds go back to the original payment method within 5 to 7 business days after we receive the item.',
  },
  {
    question: 'How can I cancel my order?',
    answer:
      'Orders can be cancelled free of charge until they are shipped. Message us with your order number and we will cancel it and refund prepaid payments.',
  },
  {
    question: 'What are your support hours?',
    answer:
      'Our team is available Monday to Saturday, 10 AM to 7 PM IST. Messages sent outside these hours are answered the next working day.',
  },
];

const SAMPLE_INSTRUCTIONS = `Tone: friendly, polite and brief. Reply in the language the customer writes in (English, Hindi or Hinglish). Keep answers to 2-3 short sentences and end with a helpful next step.

Store policies:
- Cash on delivery is available up to Rs 5,000 with a Rs 40 handling fee.
- Delivery takes 2-4 business days in metro cities and 4-7 days elsewhere in India.
- Items can be returned or exchanged within 7 days of delivery if unused and in original packaging.
- Orders can be cancelled free of charge until they ship.

Rules:
- Never promise a discount, refund amount or delivery date that is not stated above.
- Do not share another customer's information.
- If you are not sure, or the customer is upset, say a teammate will follow up instead of guessing.`;

const SAMPLE_QUESTIONS = [
  'Kya aap cash on delivery karte ho?',
  'How many days will delivery take to Mumbai?',
  'Can I return a product I do not like?',
  'Mujhe apna order cancel karna hai',
  'Where is my order?',
];

function KnowledgeCard({
  sources,
  canManage,
  onChanged,
}: {
  sources: Source[];
  canManage: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [adding, setAdding] = useState<AddKind | null>(null);
  const [seeding, setSeeding] = useState(false);

  const addSamples = async () => {
    setSeeding(true);
    try {
      for (const f of SAMPLE_FAQS) await api.post('/ai/knowledge/faq', f);
      toast.success(`Added ${SAMPLE_FAQS.length} sample FAQs`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSeeding(false);
      onChanged();
    }
  };

  const remove = async (s: Source) => {
    const ok = await confirm({
      title: `Remove “${s.title}”?`,
      message: 'The assistant will no longer use it.',
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/ai/knowledge/${s.id}`);
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Card
      title="Knowledge"
      subtitle="What the assistant may rely on besides your product catalog and orders."
      actions={
        canManage ? (
          <>
            <Button size="sm" loading={seeding} onClick={() => void addSamples()}>
              Add sample FAQs
            </Button>
            <Button size="sm" icon={<HelpCircle size={14} />} onClick={() => setAdding('faq')}>
              FAQ
            </Button>
            <Button size="sm" icon={<FileText size={14} />} onClick={() => setAdding('text')}>
              Note
            </Button>
            <Button size="sm" icon={<Globe size={14} />} onClick={() => setAdding('url')}>
              Web page
            </Button>
            <Button size="sm" icon={<Upload size={14} />} onClick={() => setAdding('pdf')}>
              PDF
            </Button>
          </>
        ) : undefined
      }
      flush
    >
      {sources.length === 0 ? (
        <EmptyState icon={<Plus size={22} />} title="Nothing added yet">
          Add your shipping, return and payment policies, FAQs, or pages and PDFs from your site.
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Type</th>
                <th>Size</th>
                <th>Added</th>
                <th className="table__actions">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.id}>
                  <td>
                    <div className="table__primary">{s.title}</div>
                    {s.sourceUrl && <div className="table__secondary">{s.sourceUrl}</div>}
                    {s.status === 'failed' && (
                      <div className="table__secondary">{s.error ?? 'Could not be read'}</div>
                    )}
                  </td>
                  <td>
                    <Badge tone={s.status === 'failed' ? 'critical' : 'neutral'}>
                      {KIND_LABEL[s.kind]}
                    </Badge>
                  </td>
                  <td>{s.chunkCount} parts</td>
                  <td>{formatRelative(s.createdAt)}</td>
                  <td className="table__actions">
                    {canManage && (
                      <Button
                        size="sm"
                        icon={<Trash2 size={14} />}
                        onClick={() => void remove(s)}
                        aria-label={`Remove ${s.title}`}
                      >
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="card__footer field__hint">
        PDFs are read for their text and the original file is not kept. Scanned PDFs without a text
        layer cannot be read.
      </p>
      {adding && (
        <AddKnowledge
          kind={adding}
          onClose={() => setAdding(null)}
          onAdded={() => {
            setAdding(null);
            onChanged();
          }}
        />
      )}
    </Card>
  );
}

function AddKnowledge({
  kind,
  onClose,
  onAdded,
}: {
  kind: AddKind;
  onClose: () => void;
  onAdded: () => void;
}) {
  const submit = useSubmit();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const file = useRef<HTMLInputElement | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      if (kind === 'faq') await api.post('/ai/knowledge/faq', { question, answer });
      else if (kind === 'text') await api.post('/ai/knowledge/text', { title, text });
      else if (kind === 'url') await api.post('/ai/knowledge/url', { url: url.trim() });
      else {
        const f = file.current?.files?.[0];
        if (!f) throw new Error('Choose a PDF file');
        if (f.size > 5 * 1024 * 1024) throw new Error('The PDF must be 5 MB or smaller');
        const bytes = new Uint8Array(await f.arrayBuffer());
        let bin = '';
        for (let i = 0; i < bytes.length; i += 0x8000) {
          bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
        }
        await api.post('/ai/knowledge/pdf', { filename: f.name, data: btoa(bin) });
      }
      return true;
    });
    if (ok) onAdded();
  };

  const titles: Record<AddKind, string> = {
    faq: 'Add an FAQ',
    text: 'Add a note',
    url: 'Add a web page',
    pdf: 'Add a PDF',
  };
  return (
    <Modal open title={titles[kind]} onClose={onClose}>
      <form className="stack" onSubmit={(e) => void onSubmit(e)}>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        {kind === 'faq' && (
          <>
            <TextField
              label="Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              error={submit.fieldErrors['question']}
              maxLength={500}
              required
            />
            <TextAreaField
              label="Answer"
              rows={4}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              error={submit.fieldErrors['answer']}
              maxLength={5000}
              required
            />
          </>
        )}
        {kind === 'text' && (
          <>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={submit.fieldErrors['title']}
              maxLength={255}
              required
            />
            <TextAreaField
              label="Text"
              rows={8}
              value={text}
              onChange={(e) => setText(e.target.value)}
              error={submit.fieldErrors['text']}
              hint="Policies, product care, store timings. Hindi, Hinglish and English all work."
              required
            />
          </>
        )}
        {kind === 'url' && (
          <TextField
            label="Page address"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={submit.fieldErrors['url']}
            hint="One public page. Its text is read once now; add it again to refresh."
            required
          />
        )}
        {kind === 'pdf' && (
          <label className="field">
            <span className="field__label">PDF file (up to 5 MB)</span>
            <input
              ref={file}
              type="file"
              accept="application/pdf,.pdf"
              className="input"
              required
            />
          </label>
        )}
        <div className="form-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={submit.pending}>
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Playground({ disabled, onUsed }: { disabled: boolean; onUsed: () => void }) {
  const submit = useSubmit();
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<Playground | null>(null);

  const ask = async (e: FormEvent) => {
    e.preventDefault();
    const r = await submit.run(() => api.post<Playground>('/ai/playground', { message }));
    if (r) {
      setResult(r);
      onUsed();
    }
  };

  return (
    <Card
      title="Try it"
      subtitle="Ask as a customer would. Nothing is sent to anyone; it uses your saved settings and counts toward the daily budget."
    >
      <form className="stack" onSubmit={(e) => void ask(e)}>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <TextAreaField
          label="Customer message"
          rows={2}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={1000}
          placeholder="e.g. Kya aap cash on delivery karte ho?"
          required
        />
        <div className="row">
          {SAMPLE_QUESTIONS.map((q) => (
            <button key={q} type="button" className="badge" onClick={() => setMessage(q)}>
              {q}
            </button>
          ))}
        </div>
        <div>
          <Button type="submit" loading={submit.pending} disabled={disabled}>
            Ask the assistant
          </Button>
        </div>
      </form>
      {result && (
        <div className="stack ai-result">
          <div className="bubble bubble--out">
            <div className="bubble__body">
              {result.reply || (result.refused ? 'The model declined to answer.' : '(no reply)')}
            </div>
          </div>
          <p className="field__hint">
            {result.toolCalls.length > 0 &&
              `It ${[...new Set(result.toolCalls.map((t) => TOOL_LABEL[t.name] ?? t.name))].join(', ')}. `}
            {result.sources.length > 0 && `Used: ${result.sources.join(' · ')}. `}
            {result.escalate && `It would hand over to a person: ${result.escalate}. `}
            {(result.inputTokens + result.outputTokens).toLocaleString()} tokens.
          </p>
        </div>
      )}
    </Card>
  );
}
